import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Globe, MapPin, Users, Shield, HeartHandshake, Star, Phone, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { useState } from 'react';
import { toast } from 'sonner';
import { visaServices, processSteps, whyChooseUs, testimonials, faqs, trustBadges, contactInfo, submitLeadForm } from '../data/mock';

const iconMap = {
  Globe,
  MapPin,
  Users,
  Shield,
  CheckCircle,
  HeartHandshake
};

const Home = () => {
  const [leadFormData, setLeadFormData] = useState({
    name: '',
    phone: '',
    email: '',
    visaType: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleLeadFormChange = (field, value) => {
    setLeadFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleLeadFormSubmit = async (e) => {
    e.preventDefault();
    if (!leadFormData.name || !leadFormData.phone || !leadFormData.email || !leadFormData.visaType) {
      toast.error('Please fill all required fields');
      return;
    }
    setIsSubmitting(true);
    const result = await submitLeadForm(leadFormData);
    setIsSubmitting(false);
    if (result.success) {
      toast.success(result.message);
      setLeadFormData({ name: '', phone: '', email: '', visaType: '' });
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 via-white to-teal-50 pt-16 pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="space-y-8 animate-in fade-in slide-in-from-left duration-700">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                Your Trusted Partner for
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-teal-600 mt-2">
                  Global Visas & Immigration
                </span>
              </h1>
              <p className="text-lg text-gray-700 leading-relaxed max-w-xl">
                Navigate your international journey with confidence. Expert guidance for New Zealand, UK, Schengen, and more. Transparent, ethical, and personalized visa solutions.
              </p>
              
              {/* Trust Badges */}
              <div className="flex flex-wrap gap-4">
                {trustBadges.slice(0, 3).map((badge, idx) => (
                  <div key={idx} className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg shadow-sm border border-gray-200">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm font-medium text-gray-700">{badge}</span>
                  </div>
                ))}
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-wrap gap-4">
                <Button asChild size="lg" className="bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700 text-white shadow-lg hover:shadow-xl transition-all text-base px-8">
                  <Link to="/eligibility">
                    Check Your Eligibility
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50 text-base px-8">
                  <a href={`tel:${contactInfo.phones.india1}`}>
                    <Phone className="mr-2 h-5 w-5" />
                    Book Free Consultation
                  </a>
                </Button>
              </div>
            </div>

            {/* Right Image */}
            <div className="relative animate-in fade-in slide-in-from-right duration-700 delay-300">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/flagged/photo-1551135049-83f3419ef05c"
                  alt="Professional visa consultation"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/30 to-transparent"></div>
              </div>
              {/* Floating Card */}
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl border border-gray-200 max-w-xs">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-teal-500 rounded-full flex items-center justify-center">
                    <CheckCircle className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">500+</p>
                    <p className="text-sm text-gray-600">Visas Processed</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Kiwi Connect Consultants
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Immigration decisions deserve expertise you can trust
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyChooseUs.map((item, idx) => {
              const IconComponent = iconMap[item.icon];
              return (
                <Card key={idx} className="border-2 border-gray-100 hover:border-blue-200 hover:shadow-lg transition-all duration-300 group">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-teal-100 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                      <IconComponent className="h-6 w-6 text-blue-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{item.title}</h3>
                    <p className="text-gray-600">{item.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Visa Services */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Our Visa Services
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Tailored visa solutions for every goal
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {visaServices.map((service, idx) => (
              <Card 
                key={service.id} 
                className={`border-2 hover:shadow-xl transition-all duration-300 group cursor-pointer ${
                  service.specialization 
                    ? 'border-blue-500 bg-gradient-to-br from-blue-50 to-teal-50' 
                    : 'border-gray-200 hover:border-blue-300'
                }`}
              >
                <CardContent className="p-6">
                  {service.specialization && (
                    <div className="mb-3">
                      <span className="bg-gradient-to-r from-blue-600 to-teal-600 text-white text-xs px-3 py-1 rounded-full font-semibold">
                        ⭐ Specialization
                      </span>
                    </div>
                  )}
                  <div className="text-3xl mb-3">{service.country.split(' ')[0]}</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{service.title}</h3>
                  <p className="text-gray-600 text-sm mb-4">{service.description}</p>
                  <div className="space-y-2 mb-4">
                    <p className="text-sm text-gray-700"><span className="font-semibold">For:</span> {service.whoFor}</p>
                    <p className="text-sm text-gray-700"><span className="font-semibold">Processing:</span> {service.processingTime}</p>
                  </div>
                  <Button asChild variant="outline" className="w-full group-hover:bg-blue-600 group-hover:text-white group-hover:border-blue-600 transition-all">
                    <Link to={`/services#${service.slug}`}>
                      Learn More
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button asChild size="lg" className="bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700 text-white">
              <Link to="/services">
                View All Services
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Our Simple 4-Step Process
            </h2>
            <p className="text-lg text-gray-600">
              Transparent and straightforward visa assistance
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {processSteps.map((step, idx) => (
              <div key={step.step} className="relative">
                <div className="bg-gradient-to-br from-blue-50 to-teal-50 rounded-xl p-6 h-full border-2 border-blue-100 hover:border-blue-300 transition-all hover:shadow-lg">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-teal-600 text-white rounded-full flex items-center justify-center text-xl font-bold mb-4">
                    {step.step}
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-gray-600 text-sm">{step.description}</p>
                </div>
                {idx < processSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2">
                    <ArrowRight className="h-6 w-6 text-blue-600" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Client Success Stories
            </h2>
            <p className="text-lg text-gray-600">
              Hear from those who trusted us with their visa journey
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, idx) => (
              <Card key={idx} className="border-2 border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all">
                <CardContent className="p-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-4 italic">"{testimonial.text}"</p>
                  <div className="border-t pt-4">
                    <p className="font-semibold text-gray-900">{testimonial.name}</p>
                    <p className="text-sm text-gray-600">{testimonial.location}</p>
                    <p className="text-xs text-blue-600 mt-1">{testimonial.visa}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Lead Capture Form */}
      <section className="py-20 bg-gradient-to-br from-blue-600 via-blue-700 to-teal-600 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Start Your Visa Journey Today
            </h2>
            <p className="text-lg text-blue-100">
              Get expert guidance tailored to your profile
            </p>
          </div>

          <Card className="border-none shadow-2xl">
            <CardContent className="p-8">
              <form onSubmit={handleLeadFormSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                    <Input
                      placeholder="Your name"
                      value={leadFormData.name}
                      onChange={(e) => handleLeadFormChange('name', e.target.value)}
                      className="border-gray-300"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                    <Input
                      type="tel"
                      placeholder="+91 XXXXX XXXXX"
                      value={leadFormData.phone}
                      onChange={(e) => handleLeadFormChange('phone', e.target.value)}
                      className="border-gray-300"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
                  <Input
                    type="email"
                    placeholder="your.email@example.com"
                    value={leadFormData.email}
                    onChange={(e) => handleLeadFormChange('email', e.target.value)}
                    className="border-gray-300"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Visa Type Interested In *</label>
                  <Select value={leadFormData.visaType} onValueChange={(value) => handleLeadFormChange('visaType', value)}>
                    <SelectTrigger className="border-gray-300">
                      <SelectValue placeholder="Select visa type" />
                    </SelectTrigger>
                    <SelectContent>
                      {visaServices.map((service) => (
                        <SelectItem key={service.id} value={service.title}>
                          {service.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button 
                  type="submit" 
                  size="lg" 
                  className="w-full bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700 text-white text-base"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Get Expert Guidance'}
                  {!isSubmitting && <ArrowRight className="ml-2 h-5 w-5" />}
                </Button>
                <p className="text-center text-sm text-gray-500">
                  🔒 Your information is secure & confidential
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600">
              Get answers to common visa queries
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, idx) => (
              <AccordionItem 
                key={idx} 
                value={`item-${idx}`}
                className="border-2 border-gray-200 rounded-lg px-6 hover:border-blue-300 transition-colors"
              >
                <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-blue-600">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-gradient-to-r from-gray-900 to-gray-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-8">
            <div>
              <h2 className="text-3xl font-bold mb-2">Ready to Begin Your Journey?</h2>
              <p className="text-gray-300">Contact us today for a free consultation</p>
            </div>
            <div className="flex flex-wrap gap-4">
              <Button asChild size="lg" className="bg-white text-gray-900 hover:bg-gray-100">
                <a href={`tel:${contactInfo.phones.india1}`}>
                  <Phone className="mr-2 h-5 w-5" />
                  Call Now
                </a>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-gray-900">
                <a href={`mailto:${contactInfo.email}`}>
                  <Mail className="mr-2 h-5 w-5" />
                  Email Us
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
